import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { Field, TextInput, TextArea, PageHeader, SaveBar, Checkbox, ColorInput, StringListEditor } from "./formKit";
import { Trash2, Plus } from "lucide-react";
import HeroSlidesSection from "./HeroSlidesSection";

const blankCardArray = (count, fields) =>
  Array.from({ length: count }, () =>
    Object.fromEntries(fields.map((f) => [f, ""]))
  );

export default function AdminHomepagePage() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await adminApi.get("/admin/homepage");
        setForm({
          hero_eyebrow: "",
          hero_headline_pre: "",
          hero_headline_accent: "",
          hero_subtitle: "",
          hero_cta_primary: "Contact us",
          hero_cta_secondary: "Learn about programs",
          approach_title: "",
          approach_subtitle: "",
          approach_cards: blankCardArray(4, ["title", "body"]),
          programs_section_title: "",
          why_title: "",
          why_subtitle: "",
          why_cards: blankCardArray(5, ["title", "body"]),
          final_cta_title: "",
          final_cta_body: "",
          promo_enabled: false,
          promo_title: "This month's special",
          promo_message: "",
          promo_button_text: "Book a trial",
          promo_button_url: "/trial-class",
          promo_start_date: "",
          promo_end_date: "",
          promo_bg_color: "#FF8C42",
          promo_text_color: "#FFFFFF",
          how_it_works_items: [],
          why_choose_items: [],
          ...data,
        });
      } catch {
        toast.error("Couldn't load homepage content.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setCard = (key, idx, field) => (v) =>
    setForm((f) => {
      const arr = [...(f[key] || [])];
      arr[idx] = { ...arr[idx], [field]: v };
      return { ...f, [key]: arr };
    });
  const addCard = (key, fields) => () =>
    setForm((f) => ({
      ...f,
      [key]: [
        ...(f[key] || []),
        Object.fromEntries(fields.map((x) => [x, ""])),
      ],
    }));
  const removeCard = (key, idx) => () =>
    setForm((f) => ({
      ...f,
      [key]: (f[key] || []).filter((_, i) => i !== idx),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/homepage", form);
      toast.success("Homepage content saved.");
    } catch {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p className="text-sm text-slate-500">Loading…</p>;

  const renderCardList = (key, fields, addLabel) => (
    <div className="grid gap-3">
      {(form[key] || []).map((card, idx) => (
        <div
          key={idx}
          className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid gap-3"
          data-testid={`homepage-${key}-${idx}`}
        >
          <div className="flex items-center justify-between">
            <span className="eyebrow text-slate-500">Card {idx + 1}</span>
            <button
              type="button"
              onClick={removeCard(key, idx)}
              className="text-slate-400 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {fields.map((f) => (
            <div key={f}>
              <span className="eyebrow text-slate-600 block mb-1.5 capitalize">
                {f}
              </span>
              {f === "body" ? (
                <TextArea
                  rows={3}
                  value={card[f]}
                  onChange={setCard(key, idx, f)}
                />
              ) : (
                <TextInput value={card[f]} onChange={setCard(key, idx, f)} />
              )}
            </div>
          ))}
        </div>
      ))}
      <button
        type="button"
        onClick={addCard(key, fields)}
        className="inline-flex items-center gap-1.5 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
      >
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </button>
    </div>
  );

  return (
    <div data-testid="admin-homepage-page">
      <PageHeader
        title="Homepage"
        subtitle="Edit the hero, Our Approach, Why-us, and final CTA blocks shown on /."
      />

      <div className="grid gap-6">
        <HeroSlidesSection />

        <section
          className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4"
          data-testid="homepage-promo-section"
        >
          <h3 className="font-display text-lg font-bold tracking-tight">
            Promo alert bar
          </h3>
          <p className="text-sm text-slate-500 -mt-2">
            Site-wide promo strip shown above the header on every public page.
          </p>
          <Checkbox
            value={form.promo_enabled}
            onChange={set("promo_enabled")}
            label="Show promo alert bar"
            hint="Turn on/off site-wide."
            data-testid="homepage-promo-enabled"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title" hint="Short label, shown bold at the start of the bar.">
              <TextInput
                value={form.promo_title}
                onChange={set("promo_title")}
                data-testid="homepage-promo-title"
              />
            </Field>
            <Field label="Message" hint="Main promo copy.">
              <TextInput
                value={form.promo_message}
                onChange={set("promo_message")}
                data-testid="homepage-promo-message"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Button text">
              <TextInput
                value={form.promo_button_text}
                onChange={set("promo_button_text")}
                data-testid="homepage-promo-button-text"
              />
            </Field>
            <Field label="Button URL" hint="Internal path (/trial-class) or full URL.">
              <TextInput
                value={form.promo_button_url}
                onChange={set("promo_button_url")}
                data-testid="homepage-promo-button-url"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Start date (optional)" hint="Promo only shows on/after this date. YYYY-MM-DD.">
              <TextInput
                value={form.promo_start_date}
                onChange={set("promo_start_date")}
                placeholder="2026-03-01"
                data-testid="homepage-promo-start-date"
              />
            </Field>
            <Field label="End date (optional)" hint="Promo hides after this date. YYYY-MM-DD.">
              <TextInput
                value={form.promo_end_date}
                onChange={set("promo_end_date")}
                placeholder="2026-03-31"
                data-testid="homepage-promo-end-date"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Bar background colour">
              <ColorInput
                value={form.promo_bg_color}
                onChange={set("promo_bg_color")}
                data-testid="homepage-promo-bg-color"
              />
            </Field>
            <Field label="Bar text colour">
              <ColorInput
                value={form.promo_text_color}
                onChange={set("promo_text_color")}
                data-testid="homepage-promo-text-color"
              />
            </Field>
          </div>
        </section>

        <section
          className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4"
          data-testid="homepage-simplified-section"
        >
          <h3 className="font-display text-lg font-bold tracking-tight">
            Why parents choose Nexora
          </h3>
          <p className="text-sm text-slate-500 -mt-2">
            Short list shown on the homepage "Why parents choose Nexora" section.
          </p>
          <Field label="Items">
            <StringListEditor
              value={form.why_choose_items}
              onChange={set("why_choose_items")}
              placeholder="Add a reason"
            />
          </Field>
          <h3 className="font-display text-lg font-bold tracking-tight mt-4">
            How it works
          </h3>
          <p className="text-sm text-slate-500 -mt-2">
            3 simple steps shown on the homepage.
          </p>
          {(form.how_it_works_items || []).map((step, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid gap-3"
              data-testid={`homepage-how-step-${idx}`}
            >
              <span className="eyebrow text-slate-500">Step {idx + 1}</span>
              <Field label="Title">
                <TextInput
                  value={step.title || ""}
                  onChange={(v) => {
                    const next = [...form.how_it_works_items];
                    next[idx] = { ...next[idx], title: v };
                    set("how_it_works_items")(next);
                  }}
                />
              </Field>
              <Field label="Description">
                <TextArea
                  rows={2}
                  value={step.description || ""}
                  onChange={(v) => {
                    const next = [...form.how_it_works_items];
                    next[idx] = { ...next[idx], description: v };
                    set("how_it_works_items")(next);
                  }}
                />
              </Field>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              set("how_it_works_items")([
                ...(form.how_it_works_items || []),
                { title: "", description: "" },
              ])
            }
            className="self-start text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            + Add step
          </button>
        </section>

        <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight">Hero</h3>
          <Field label="Eyebrow (small label above headline)">
            <TextInput
              value={form.hero_eyebrow}
              onChange={set("hero_eyebrow")}
              data-testid="homepage-hero-eyebrow"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Headline (regular)">
              <TextInput
                value={form.hero_headline_pre}
                onChange={set("hero_headline_pre")}
                data-testid="homepage-hero-headline-pre"
              />
            </Field>
            <Field label="Headline (accent — blue)">
              <TextInput
                value={form.hero_headline_accent}
                onChange={set("hero_headline_accent")}
                data-testid="homepage-hero-headline-accent"
              />
            </Field>
          </div>
          <Field label="Subtitle / paragraph">
            <TextArea
              rows={4}
              value={form.hero_subtitle}
              onChange={set("hero_subtitle")}
              data-testid="homepage-hero-subtitle"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Primary CTA text">
              <TextInput
                value={form.hero_cta_primary}
                onChange={set("hero_cta_primary")}
              />
            </Field>
            <Field label="Secondary CTA text">
              <TextInput
                value={form.hero_cta_secondary}
                onChange={set("hero_cta_secondary")}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Our approach
          </h3>
          <Field label="Section title">
            <TextInput
              value={form.approach_title}
              onChange={set("approach_title")}
            />
          </Field>
          <Field label="Section subtitle">
            <TextArea
              rows={3}
              value={form.approach_subtitle}
              onChange={set("approach_subtitle")}
            />
          </Field>
          <div>
            <span className="eyebrow text-slate-600 block mb-2">
              Approach cards
            </span>
            {renderCardList("approach_cards", ["title", "body"], "Add approach card")}
          </div>
        </section>

        <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Programs section
          </h3>
          <Field label="Section title">
            <TextInput
              value={form.programs_section_title}
              onChange={set("programs_section_title")}
            />
          </Field>
        </section>

        <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Why families choose us
          </h3>
          <Field label="Section title">
            <TextInput value={form.why_title} onChange={set("why_title")} />
          </Field>
          <Field label="Section subtitle">
            <TextArea
              rows={3}
              value={form.why_subtitle}
              onChange={set("why_subtitle")}
            />
          </Field>
          <div>
            <span className="eyebrow text-slate-600 block mb-2">Why-us cards</span>
            {renderCardList("why_cards", ["title", "body"], "Add why-us card")}
          </div>
        </section>

        <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Final CTA
          </h3>
          <Field label="Title">
            <TextInput
              value={form.final_cta_title}
              onChange={set("final_cta_title")}
            />
          </Field>
          <Field label="Body">
            <TextArea
              rows={4}
              value={form.final_cta_body}
              onChange={set("final_cta_body")}
            />
          </Field>
        </section>
      </div>

      <SaveBar onSave={save} saving={saving} />
    </div>
  );
}
